import { notify } from "@general-backoffice/core";
import type { User } from "@investhinks/api-client";
import classnames from "classnames";
import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  Badge,
  Button,
  Col,
  Collapse,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  ListGroup,
  ListGroupItem,
  Media,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  Navbar,
  NavItem,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import { usersEndpoints } from "../../api";
import { useAdminViewingUser } from "../../contexts/AdminViewingUserContext";
import { returnToAdmin } from "../../redux/reducers/session/actions";
import type { RootState } from "../../redux/reducers/session/state";
import { startImpersonation } from "../../utils/impersonation";
import isTokenExpiredOrInvalid from "../../utils/isTokenExpiredOrInvalid";

// Local type for translation function
type TranslationFn = (key: string, options?: Record<string, unknown>) => string;

// Local type definitions for UI components
interface NotificationItem {
  eventId?: string | number;
  isRead?: boolean;
  onClick?: (e?: React.MouseEvent) => void;
  imgSrc?: string;
  title?: React.ReactNode;
  subtitle?: string;
  description?: React.ReactNode;
}

interface NotificationsProps {
  notificationsHeader?: React.ReactNode;
  notifications?: NotificationItem[];
  notificationsCount?: number;
  onMarkEventRead?: (eventId: string | number) => void;
}

interface NotificationProps extends NotificationItem {
  onMarkRead?: (eventId: string | number) => void;
  markAsReadTooltip?: string;
}

interface SessionOption {
  onClick?: () => void;
  icon?: string;
  title?: string;
  useDivider?: boolean;
}

interface SessionProps {
  avatarSrc?: string;
  sessionName?: React.ReactNode;
  sessionOptions?: SessionOption[];
  sessionOptionsHeader?: string;
}

interface ImpersonationIndicatorProps {
  isImpersonating: boolean;
  user?: User | null;
  isReturning: boolean;
  onReturn: () => void;
  t: TranslationFn;
}

interface SessionHeaderProps extends NotificationsProps, SessionProps {
  setOpen?: (open: boolean) => void;
  isOpen?: boolean;
  isDark?: boolean;
  color?: string;
}

// Notifications Component with badge support
const Notifications: React.FC<NotificationsProps> = ({
  notificationsHeader,
  notifications = [],
  notificationsCount = 0,
  onMarkEventRead,
}) => {
  const { t } = useTranslation("layout", { keyPrefix: "sessionHeader.notifications" });
  const hasNotifications = notifications.length > 0;

  return (
    <UncontrolledDropdown nav>
      <DropdownToggle className="nav-link cursor-pointer" color="" tag="button" type="button" aria-label={t("ariaLabel")}>
        <div className="position-relative">
          <i className={classnames("ni ni-bell-55", { "text-warning": hasNotifications })} />
          {notificationsCount > 0 && (
            <Badge
              color="danger"
              pill
              style={{
                position: "absolute",
                top: "-8px",
                right: "-10px",
                fontSize: "0.6rem",
                minWidth: "16px",
                height: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 4px",
              }}
            >
              {notificationsCount > 99 ? "99+" : notificationsCount}
            </Badge>
          )}
        </div>
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-xl py-0 overflow-hidden" right>
        <div className="px-3 py-3">
          <h6 className="text-sm text-muted m-0">{notificationsHeader}</h6>
        </div>
        {hasNotifications && (
          <ListGroup flush style={{ maxHeight: "400px", overflowY: "auto" }}>
            {notifications.map((notification, index) => (
              <Notification
                {...notification}
                onMarkRead={onMarkEventRead}
                key={index}
                markAsReadTooltip={t("markAsRead")}
              />
            ))}
          </ListGroup>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

const Notification: React.FC<NotificationProps> = ({
  eventId,
  isRead,
  onClick,
  imgSrc,
  title,
  subtitle,
  description,
  onMarkRead,
  markAsReadTooltip,
}) => {
  const [isHovered, setIsHovered] = React.useState<boolean>(false);

  const handleMarkRead = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMarkRead && eventId && !isRead) {
      onMarkRead(eventId);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Mark as read when clicking the notification
    if (onMarkRead && eventId && !isRead) {
      onMarkRead(eventId);
    }
    // Also trigger the original onClick (navigate)
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <ListGroupItem
      className={classnames("list-group-item-action cursor-pointer", { "bg-secondary": isRead })}
      onClick={handleClick}
      tag="button"
      type="button"
      style={{ opacity: isRead ? 0.6 : 1 }}
    >
      <Row className="align-items-center">
        <Col className="col-auto">
          {!!imgSrc && <img alt="..." className="avatar rounded-circle" src={imgSrc} />}
          {!imgSrc && <i className="fas fa-bell" />}
        </Col>
        <div className="col ml--2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0 text-sm">{title}</h4>
            </div>
            <div className="text-right text-muted">
              <small>{subtitle}</small>
            </div>
          </div>
          <p className="text-sm mb-0">{description}</p>
        </div>
        {!isRead && eventId && (
          <Col className="col-auto">
            <button
              type="button"
              className={classnames(
                "btn btn-sm rounded-circle d-flex align-items-center justify-content-center",
                isHovered ? "btn-success" : "btn-outline-success"
              )}
              style={{
                width: "36px",
                height: "36px",
                padding: 0,
                transition: "all 0.2s ease",
              }}
              onClick={handleMarkRead}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              title={markAsReadTooltip}
              aria-label={markAsReadTooltip}
            >
              <i className="fas fa-check" style={{ fontSize: "1rem" }} aria-hidden="true" />
            </button>
          </Col>
        )}
      </Row>
    </ListGroupItem>
  );
};

// Session Component
const Session: React.FC<SessionProps> = ({ avatarSrc, sessionName, sessionOptions = [], sessionOptionsHeader }) => {
  const hasOptions = sessionOptions.length > 0;

  return (
    <UncontrolledDropdown nav>
      <DropdownToggle className="nav-link pr-0 cursor-pointer" color="" tag="button" type="button">
        <Media className="align-items-center">
          {!!avatarSrc && (
            <span className="avatar avatar-sm rounded-circle">
              <img alt="..." src={avatarSrc} />
            </span>
          )}
          {!avatarSrc && <i className="fas fa-user-circle" style={{ fontSize: 36 }} />}
          <Media className="ml-2 d-none d-lg-block">
            <span className="mb-0 text-sm font-weight-bold">{sessionName}</span>
          </Media>
        </Media>
      </DropdownToggle>
      {hasOptions && (
        <DropdownMenu right>
          <DropdownItem className="noti-title" header tag="div">
            {!!sessionOptionsHeader && <h6 className="text-overflow m-0">{sessionOptionsHeader}</h6>}
          </DropdownItem>
          {sessionOptions.map(({ onClick, icon, title, useDivider = false }, index) => (
            <React.Fragment key={index}>
              {useDivider && <DropdownItem divider />}
              <DropdownItem className="cursor-pointer" onClick={onClick} tag="button" type="button">
                <i className={icon} aria-hidden="true" />
                <span>{title}</span>
              </DropdownItem>
            </React.Fragment>
          ))}
        </DropdownMenu>
      )}
    </UncontrolledDropdown>
  );
};

// Hook to get impersonation state and actions
const useImpersonation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("layout", { keyPrefix: "sessionHeader.impersonation" });
  const { adminToken, user } = useSelector((state: RootState) => state.sessionState);
  const [isReturning, setIsReturning] = useState<boolean>(false);

  const isImpersonating = !!adminToken;

  const handleReturnToAdmin = async () => {
    setIsReturning(true);
    try {
      dispatch(returnToAdmin());
      const { data: adminUser } = await usersEndpoints.getMe({ populate: ["role"] });

      if (adminUser) {
        dispatch({
          type: "LOGIN",
          payload: {
            token: adminToken,
            user: adminUser,
          },
        });
        notify(t("returnedSuccess"), "success");
        navigate("/");
      }
    } catch (error: unknown) {
      console.error("Error returning to admin mode:", error);
      if (isTokenExpiredOrInvalid(error)) {
        notify(t("sessionExpired"), "error");
        dispatch({ type: "LOGOUT" });
      } else {
        notify(t("returnError"), "error");
      }
    }
    setIsReturning(false);
  };

  return {
    isImpersonating,
    user,
    isReturning,
    handleReturnToAdmin,
    t: ((key: string, options?: Record<string, unknown>): string => {
      const result = t(key, options ?? {});
      return String(result);
    }) satisfies TranslationFn,
  };
};

// Impersonation Indicator Component (shows when admin is impersonating a user)
const ImpersonationIndicator: React.FC<ImpersonationIndicatorProps> = ({
  isImpersonating,
  user,
  isReturning,
  onReturn,
  t,
}) => {
  if (!isImpersonating) return null;

  return (
    <NavItem className="d-flex align-items-center mr-4">
      <div className="d-flex align-items-center">
        <i className="fas fa-user-secret text-white mr-3" style={{ fontSize: "1.25rem" }} />
        <span className="text-white mr-3" style={{ fontSize: "1rem", fontWeight: 600 }}>
          {t("viewingAs")} <strong>{user?.name || user?.email}</strong>
        </span>
        <Button color="light" onClick={onReturn} disabled={isReturning} className="ml-2" style={{ fontWeight: 700 }}>
          {isReturning ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" />
              {t("returning")}
            </>
          ) : (
            <>
              <i className="fas fa-undo mr-2" />
              {t("returnToAdmin")}
            </>
          )}
        </Button>
      </div>
    </NavItem>
  );
};

// Impersonate Button Component
const ImpersonateButton: React.FC = () => {
  const { t } = useTranslation("layout", { keyPrefix: "sessionHeader.impersonation" });
  const { viewingUser } = useAdminViewingUser();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  if (!viewingUser) return null;

  const userName = viewingUser?.name || viewingUser?.email;

  const handleImpersonate = async () => {
    setIsImpersonating(true);
    const success = await startImpersonation(viewingUser.id, dispatch, navigate);
    setIsImpersonating(false);

    if (success) {
      setShowModal(false);
    }
  };

  return (
    <>
      <NavItem className="d-none d-md-flex align-items-center mr-3">
        <Button
          color="warning"
          size="sm"
          onClick={() => setShowModal(true)}
          className="d-flex align-items-center"
          style={{ whiteSpace: "nowrap" }}
        >
          <i className="fas fa-user-secret mr-2" />
          <span className="d-none d-lg-inline">{t("loginAs", { name: userName?.split(" ")[0] || "user" })}</span>
          <span className="d-lg-none">{t("impersonate")}</span>
        </Button>
      </NavItem>

      <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
        <ModalHeader toggle={() => setShowModal(false)}>{t("confirmTitle")}</ModalHeader>
        <ModalBody>
          <Trans
            i18nKey="sessionHeader.impersonation.confirmMessage"
            ns="layout"
            values={{ name: userName }}
            components={{ strong: <strong /> }}
          />
          <div className="mt-3 text-muted small">{t("confirmNote")}</div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowModal(false)} disabled={isImpersonating}>
            {t("cancel")}
          </Button>
          <Button color="warning" onClick={handleImpersonate} disabled={isImpersonating}>
            {isImpersonating ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1" />
                {t("loading")}
              </>
            ) : (
              <>
                <i className="fas fa-user-secret mr-1" />
                {t("loginAsUser")}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

// Main SessionHeader Component
const SessionHeader: React.FC<SessionHeaderProps> = (props) => {
  const { setOpen, isOpen = true, isDark = true, color = "default", notificationsCount = 0, onMarkEventRead } = props;

  const { isImpersonating, user, isReturning, handleReturnToAdmin, t } = useImpersonation();
  const { t: tLayout } = useTranslation("layout", { keyPrefix: "sessionHeader" });
  const toggleSidenav = () => setOpen?.(!isOpen);
  const isAdmin = user?.role?.name === "Admin";

  return (
    <Navbar
      className={classnames(
        "navbar-top navbar-expand border-bottom",
        { [`bg-${color}`]: !isImpersonating },
        { "navbar-dark": isDark || isImpersonating },
        { "navbar-light": !isDark && !isImpersonating },
        { "navbar-impersonating": isImpersonating }
      )}
    >
      <Container fluid>
        <Collapse navbar isOpen={true}>
          <Nav className="align-items-center ml-md-auto" navbar>
            {/* Was d-xl-none, which hid the pin toggle in exactly the range
                where the collapsed sidebar expands on hover over the content. */}
            <NavItem>
              <button
                type="button"
                className={classnames(
                  "pr-3 sidenav-toggler border-0 bg-transparent",
                  { active: isOpen },
                  { "sidenav-toggler-dark": isDark || isImpersonating }
                )}
                onClick={toggleSidenav}
                aria-expanded={!!isOpen}
                aria-label={tLayout("toggleMenu")}
              >
                <div className="sidenav-toggler-inner">
                  <i className="sidenav-toggler-line" aria-hidden="true" />
                  <i className="sidenav-toggler-line" aria-hidden="true" />
                  <i className="sidenav-toggler-line" aria-hidden="true" />
                </div>
              </button>
            </NavItem>
            <ImpersonationIndicator
              isImpersonating={isImpersonating}
              user={user}
              isReturning={isReturning}
              onReturn={handleReturnToAdmin}
              t={t}
            />
            {!isImpersonating && <ImpersonateButton />}
            {!isAdmin && (
              <Notifications {...props} notificationsCount={notificationsCount} onMarkEventRead={onMarkEventRead} />
            )}
          </Nav>

          <Nav className="align-items-center ml-auto ml-md-0" navbar>
            <Session {...props} />
          </Nav>
        </Collapse>
      </Container>
    </Navbar>
  );
};

export default SessionHeader;
